<template>
    <z-modal
        class="medial-library-modal"
        theme="library"
        id="mediaLibraryPopup"
        overlay="linear-gradient(0deg, rgba(0, 76, 151, 0.44) 0%, rgba(0, 76, 151, 0.44) 100%), rgba(0, 0, 0, 0.50)"
    >
        <div class="medial-library-modal__content">
            <div
                ref="swiperContainer"
                class="swiper-container"
            >
               <div class="swiper-wrapper">
                   <div
                       class="swiper-slide"
                       v-for="(item, index) in data"
                       :key="index"
                   >
                       <z-video
                           v-if="item && item.mlVideoLink.value"
                           ref="video"
                           :muted="false"
                           :src="item.mlVideoLink.value"
                           :autoplay="false"
                           :poster="item.previewPicture.SRC"
                       />
                       <img
                           v-else-if="item && item.previewPicture.SRC"
                           ref="image"
                           :data-src="item.previewPicture.SRC"
                           :alt="item.name"
                           class="swiper-lazy"
                       />
                       <z-preloader v-else />
                   </div>

               </div>
           </div>
        </div>
        <div class="medial-library-modal__bottom">
            <p
                v-if="currentItem && currentItem.name"
                class="medial-library-modal__name"
                v-html="currentItem.name"
            />
            <div v-if="data.length > 1" class="medial-library-modal__navigation">
                <div class="medial-library-modal__navigation-count">
                    <span>{{ formatNumber(currentIndex + 1) }}</span>
                    <span>/{{ formatNumber(data.length) }}</span>
                </div>
                <div class="medial-library-modal__navigation-buttons">
                    <div
                        class="medial-library-modal__button medial-library-modal__button--prev"
                        :disabled="currentIndex === 0"
                    >
                        <z-icon
                            name="round-arrow-folder/arrow"
                            width="24"
                            height="24"
                            dir="left"
                        />
                    </div>
                    <div
                        class="medial-library-modal__button medial-library-modal__button--next"
                        :disabled="currentIndex === data.length - 1"
                    >
                        <z-icon
                            name="round-arrow-folder/arrow"
                            width="24"
                            height="24"
                            dir="right"
                        />
                    </div>
                </div>
            </div>
        </div>
    </z-modal>
</template>
<script>
import Swiper from 'swiper'
export default {
    name: 'MediaLibraryModal',
    props: {
        data: {
            type: Array,
            default: () => []
        }
    },
    data () {
        return {
            currentIndex: 0,
            videoLoaded: false,
            imgLoaded: false,
            videoSlideIndexes: []
        }
    },
    mounted () {
        this.$root.$bus.$on('open-modal', data => this.openModal(data))
    },
    computed: {
        currentItem () {
            return this.data[this.currentIndex]
        }
    },
    methods: {
        openModal (data) {
            if (data.id !== 'mediaLibraryPopup') return

            this.currentIndex = data.index

            this.videoSlideIndexes = this.data.map((item, index) => {
                return item.mlVideoLink.value ? index : null
            }).filter(index => index !== null)

            this.$nextTick(() => {
                this.initSwiper()
                if (this.swiper) {
                    this.swiper.slideTo(this.currentIndex, 0)
                }
            })
        },
        formatNumber (number) {
            return number < 10 ? `0${number}` : number
        },
        playVideo (index) {
            const refIndex = this.videoSlideIndexes?.indexOf(index)
            const videoComponent = this.$refs.video?.[refIndex]
            if (videoComponent && typeof videoComponent.play === 'function') {
                this.$nextTick(() => {
                    videoComponent.play()
                })
            }
        },
        pauseVideo (index) {
            const refIndex = this.videoSlideIndexes?.indexOf(index)
            const videoComponent = this.$refs.video?.[refIndex]

            if (videoComponent && typeof videoComponent.pause === 'function') {
                videoComponent.pause()
            }
        },
        initSwiper () {
            this.swiper = new Swiper(this.$refs.swiperContainer, {
                initialSlide: this.currentIndex,
                grabCursor: true,
                effect: 'fade',
                fadeEffect: {
                    crossFade: true
                },
                keyboard: {
                    enabled: true,
                    onlyInViewport: true
                },
                navigation: {
                    nextEl: '.medial-library-modal__button--next',
                    prevEl: '.medial-library-modal__button--prev'
                },
                lazy: {
                    loadPrevNext: true
                },
                on: {
                    slideChange: () => {
                        const previousIndex = this.currentIndex
                        this.currentIndex = this.swiper.activeIndex
                        this.pauseVideo(previousIndex)
                        this.playVideo(this.currentIndex)
                    }
                }
            })
            this.$nextTick(() => {
                this.playVideo(this.currentIndex)
            })
        }
    }
}
</script>

<style lang="scss">
.medial-library-modal {
    backdrop-filter: blur(10px);

    &__content {
        margin-bottom: $token-spacers-2-s;
        padding-bottom: 56.25%;
        height: 0;
        border-radius: 40px 40px 40px 0;
        overflow: hidden;

        @include breakpoint(laptop) {
            border-radius: 24px 24px 24px 0;
        }
        @include breakpoint(tablet) {
            border-radius: 20px 20px 20px 0;
        }

        @include breakpoint(mobile) {
          gap: $token-spacers-xs;
        }

        & img {
            max-width: 100%;
            width: 100%;
        }
    }

    &__bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: $token-spacers-4-xl;

        @include breakpoint(laptop) {
          gap: $token-spacers-2-xl;
        }
        @include breakpoint(mobile) {
          flex-direction: column;
          gap: $token-spacers-xs;
        }
    }

    &__name {
        margin: 0;
        font-size: $token-typo-3-xl-desktop-size;
        font-weight: 600;
        line-height: 1.2;
        letter-spacing: -0.64px;
        color: #fff;
        @include breakpoint(laptop) {
          font-size: $token-typo-2-xl-desktop-size;
        }
    }

    &__navigation {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: $token-spacers-xs;
    }

    &__navigation-count {
        font-family: $robotoMono;
        font-size: 24px;
        font-weight: 300;
        line-height: 1.6;
        letter-spacing: 0.28px;
        color: #fff;
        white-space: nowrap;

        & span:first-of-type {
            font-size: 28px;
        }
    }

    &__navigation-buttons {
        display: flex;
        gap: $token-spacers-2-xs;
    }

    &__button {
        width: 72px;
        height: 72px;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(255, 255, 255, 0.40);
        border-radius: 50%;
        cursor: pointer;
        transition: background 0.3s;
        &[disabled] {
            pointer-events: none;
        }
        &:hover {
            background: #fff;
            & .z-icon path {
                fill: $token-colors-blue;
            }
        }

        & .z-icon path {
            fill: #fff;
        }

        @include breakpoint(laptop) {
            width: 52px;
            height: 52px;

            & .z-icon {
                width: 18px;
                height: 18px;
            }
        }
        @include breakpoint(tablet) {
            width: 42px;
            height: 42px;

            & .z-icon {
                width: 14px;
                height: 14px;
            }
        }
        @include breakpoint(v-tablet) {
            width: 40px;
            height: 40px;
        }
    }

    & .z-modal__close {
        display: none;
    }

    & .z-modal__container {
        max-width: 1462px;
        background: transparent;
        box-shadow: none;

        @include breakpoint(laptop) {
            padding: 0 $token-spacers-2-s;
            max-width: 1240px
        }
        @include breakpoint(v-tablet) {
            padding: 0 $token-spacers-s;
        }
        @include breakpoint(mobile) {
            padding: 0 $token-spacers-2-xs;
        }
    }

    & .modal-html {
        padding: 0;
    }
}
</style>
